import { seaArea } from './seaArea';

/** 25.77, -79.6 → "25°46.200′N 79°36.000′W", the degrees-and-minutes form charts and plotters use. */
export const toDegreesMinutes = (lat: number, lon: number, separator = ' ') => {
  const part = (v: number, pos: string, neg: string) => {
    const minutes = Math.round(Math.abs(v) * 60_000) / 1000;
    const deg = Math.floor(minutes / 60);
    return `${deg}°${(minutes - deg * 60).toFixed(3).padStart(6, '0')}′${v < 0 ? neg : pos}`;
  };
  return `${part(lat, 'N', 'S')}${separator}${part(lon, 'E', 'W')}`;
};

const KNOTS_PER_MS = 3600 / 1852;
// ponytail: below this a GPS course is noise — a boat at anchor swings through every heading.
// Raise it if drifting boats log a course nobody steered.
const MIN_COURSE_KN = 1;

/**
 * A GPS fix's speed and course over ground as a log writes them, "6.2 kn" and "045°T"; empty
 * where the fix has none. iOS reports an unknown speed as -1, the web often as null.
 */
export const speedAndCourse = (speed: number | null, course: number | null | undefined) => {
  const knots = speed !== null && speed >= 0 ? speed * KNOTS_PER_MS : undefined;
  const steady = knots !== undefined && knots >= MIN_COURSE_KN;
  return {
    speed: knots === undefined ? '' : `${knots.toFixed(1)} kn`,
    course:
      steady && typeof course === 'number' && course >= 0
        ? `${String(Math.round(course) % 360).padStart(3, '0')}°T`
        : '',
  };
};

/**
 * A position in words, as a log names a place: the address ashore; offshore — where Nominatim
 * knows a state at best and past ~20 km nothing — the sea (offline, from the bundle) and the
 * position, as a chart plotter would. Offline at sea the lookup fails and the sea and position
 * still come back.
 */
export const describePosition = async (latitude: number, longitude: number) => {
  let data: { place_id?: number; address?: Record<string, string> } = {};
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`;
    data = await (await fetch(url, { headers: { 'Accept-Language': 'en' } })).json();
  } catch (err) {
    console.error('[describePosition] error:', err);
  }

  const address = data.address ?? {};
  const street = [address.house_number, address.road].filter(Boolean).join(' ');
  const locality =
    address.city ?? address.town ?? address.village ?? address.hamlet ?? address.suburb;
  const region = address.state ?? address.county;
  const formattedAddress =
    street || locality
      ? [street, address.neighbourhood, locality, region].filter(Boolean).join(', ')
      : [await seaArea(latitude, longitude), toDegreesMinutes(latitude, longitude)]
          .filter(Boolean)
          .join(', ');

  return { placeId: data.place_id, address, formattedAddress };
};
